/**
 * Map service-request external goods onto vendor-invoice line items.
 * Matches Laravel VendorTransactionController::vendorInvoiceExternalGoods
 * so the on-screen invoice total agrees with the voucher list / WhatsApp PDF.
 *
 * Stored rows are typically { name, quantity, total, type }. Internal rows
 * (Rajput stock) are skipped — only vendor-purchased external goods go on
 * the Rajdoot bill.
 *
 * @param {Array} meta
 * @param {number} storedTotal
 * @returns {Array<{description: string, quantity: number, laborCost: number, discount: number}>}
 */
export function externalGoodsInvoiceLines(meta, storedTotal = 0) {
  let rows = meta;
  if (typeof rows === "string") {
    try {
      rows = JSON.parse(rows);
    } catch (e) {
      rows = [];
    }
  }
  if (!Array.isArray(rows)) {
    rows = [];
  }

  const lines = [];
  let goodsTotal = 0;

  rows.forEach(row => {
    if (!row || typeof row !== "object") return;
    const type = String(row.type || "external").toLowerCase();
    if (type === "internal") return;

    let qty = parseFloat(row.quantity) || 0;
    let rate = parseFloat(row.rate != null ? row.rate : row.price) || 0;
    let lineTotal =
      row.total != null && row.total !== ""
        ? parseFloat(row.total) || 0
        : qty * rate;

    if (qty <= 0 && lineTotal <= 0 && rate <= 0) return;
    if (qty <= 0) qty = 1;
    if (rate <= 0 && lineTotal > 0) rate = lineTotal / qty;

    goodsTotal += lineTotal;
    lines.push({
      description: row.name || row.description || "External goods",
      quantity: qty,
      laborCost: rate,
      discount: 0
    });
  });

  const stored = parseFloat(storedTotal) || 0;
  if (lines.length === 0 && stored > 0) {
    lines.push({
      description: "External goods",
      quantity: 1,
      laborCost: stored,
      discount: 0
    });
  }

  return lines;
}

export function vendorInvoiceLineItems({
  labors = [],
  externalGoodsMeta,
  externalGoodsTotal = 0,
  laborDiscount = 0
} = {}) {
  const laborLines = (labors || []).map(item => ({
    description: item.description,
    quantity: parseFloat(item.quantity) || 0,
    laborCost: parseFloat(item.laborCost != null ? item.laborCost : item.labor_cost) || 0,
    discount: parseFloat(item.discount) || 0
  }));

  const goodsLines = externalGoodsInvoiceLines(
    externalGoodsMeta,
    externalGoodsTotal
  );

  const lines = laborLines.concat(goodsLines);
  const discount = parseFloat(laborDiscount) || 0;
  if (discount > 0) {
    lines.push({
      description: "Discount",
      quantity: 1,
      laborCost: discount,
      discount,
      isDiscount: true
    });
  }
  return lines;
}

export function vendorInvoiceTotal(lines = []) {
  return (lines || []).reduce((sum, item) => {
    if (item && item.isDiscount) {
      return sum - (parseFloat(item.discount) || 0);
    }
    const cost = parseFloat(item.laborCost) || 0;
    const qty = parseFloat(item.quantity) || 0;
    const discount = parseFloat(item.discount) || 0;
    return sum + cost * qty - discount;
  }, 0);
}
